// Acquired? — the "found, not confirmed" marker (2026-10-07).
//
// A company_acquisitions row with no decision is something a person must
// confirm: a buyer's portfolio page, the ownership check, a web signal or
// Apollo says the company was bought. Until now it waited only on Buyers →
// Acquisitions — K&H Home Solutions' "acquired by D5 Capital Holdings" (its
// owner's own holding company) went through without the dealmaker who knew
// the owner ever seeing it. This pill sits in the company header and the deal
// header and opens a drawer with every pending record — who bought it, per
// whom, the sentence, the page — and Confirm / Not it. Confirm is the same
// close-out as everywhere else (live deals exit as Company sold, a note is
// filed), so it asks first when a deal would exit.

const ACQ_SOURCE_LABEL = {
  portfolio_page: 'portfolio page', ownership: 'ownership check', web_signal: 'web signal', apollo: 'Apollo',
  website_pick: 'website read', manual: 'by hand', exit_handler: 'deal exit', dc_tag: 'DealCloud tag',
};
const ACQ_CONFIDENCE_HUE = { high: 'green', medium: 'amber', low: 'slate' };
const acqAgo = (iso) => {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : d < 30 ? `${d} days ago` : `${Math.round(d / 30)} months ago`;
};

// Pending records for one company. `enabled` false (an acquired company, an
// exited deal) skips the request and reports nothing.
const useAcquisitionsToConfirm = (companyId, enabled = true) => {
  const [rows, setRows] = React.useState([]);
  const load = React.useCallback(async () => {
    if (!companyId || !enabled) { setRows([]); return; }
    try {
      const r = await window.AtlasApi.acquisitionsToConfirm(companyId);
      setRows((r && r.items) || []);
    } catch (_) { setRows([]); }
  }, [companyId, enabled]);
  React.useEffect(() => { load(); }, [load]);
  return [rows, load];
};

const AcquisitionConfirmDrawer = ({ open, companyName, rows, onClose, onDecided, zIndex }) => {
  const [busy, setBusy] = React.useState(null);
  const decide = async (r, decision) => {
    // Confirm exits the company's live deals as "Company sold" (final) — say
    // so before doing it, never after. Same words as Buyers → Acquisitions.
    if (decision === 'confirmed' && r.live_deals > 0) {
      const ok = await window.showConfirm({
        title: 'Confirm the acquisition?',
        body: `This marks ${r.company_name} acquired${r.acquirer_name ? ` by ${r.acquirer_name}` : ''} and exits its `
          + `${r.live_deals === 1 ? 'live engagement' : `${r.live_deals} live engagements`} as "Company sold". `
          + `A note with the source goes on the company${r.live_deals === 1 ? ' and the engagement' : ''}.`,
        confirmLabel: r.live_deals === 1 ? 'Confirm & exit deal' : 'Confirm & exit deals',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(r.id);
    try {
      const out = await window.AtlasApi.decideBuyerAcquisition(r.id, decision);
      if (decision === 'confirmed') window.acquisitionToast?.(r, out);
      else window.pushToast?.({ type: 'success', title: 'Not an acquisition', body: `${r.company_name} stays as it was.` });
      if (onDecided) await onDecided(decision, r);
    } catch (e) {
      window.pushToast?.({ type: 'error', title: 'Could not save', body: e.message || String(e) });
    } finally { setBusy(null); }
  };
  const list = rows || [];
  return (
    <window.Drawer open={open} onClose={onClose} title="Acquired?" subtitle={companyName || ''} width={520} zIndex={zIndex}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.map((r) => (
          <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13 }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                {r.acquirer_name
                  ? <>by {r.acquirer_company_id
                      ? <a href={`#/companies/${r.acquirer_company_id}`} style={{ color: 'var(--accent-text)' }}>{r.acquirer_name}</a>
                      : r.acquirer_name}</>
                  : 'Acquirer not named'}
              </span>
              {r.announced_year && <span style={{ color: 'var(--text-3)' }}>· {r.announced_year}</span>}
              <window.Pill hue={ACQ_CONFIDENCE_HUE[r.confidence] || 'slate'} sz="sm">{r.confidence}</window.Pill>
              <window.Pill hue="slate" sz="sm">{ACQ_SOURCE_LABEL[r.source] || r.source}</window.Pill>
              {r.live_deals > 0 && <window.Pill hue="rose" sz="sm">{r.live_deals} live deal{r.live_deals === 1 ? '' : 's'}</window.Pill>}
            </div>
            {r.evidence && <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5 }}>{r.evidence}</div>}
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span>{acqAgo(r.first_seen_at)}</span>
              {r.source_url && (
                <a href={r.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>
                  {r.source_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}
                </a>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <window.Btn size="xs" variant="accent" disabled={busy === r.id} onClick={() => decide(r, 'confirmed')}
                title={r.live_deals > 0
                  ? 'Mark acquired — exits the live deal as Company sold and logs a note'
                  : 'Mark acquired — the company leaves every prospect list; logs a note'}>
                {busy === r.id ? '…' : 'Confirm'}
              </window.Btn>
              <window.Btn size="xs" variant="ghost" disabled={busy === r.id} onClick={() => decide(r, 'rejected')}
                title="Wrong company, or not an acquisition">Not it</window.Btn>
            </div>
          </div>
        ))}
        {list.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Nothing left to confirm.</div>}
      </div>
    </window.Drawer>
  );
};

// One element for a header: the pill, only while something is pending, and
// its drawer. Owns the fetch, so the company page and the deal page mount it
// the same way. `hidden` for an acquired company or an exited deal.
const AcquisitionHeaderPill = ({ companyId, companyName, hidden = false, onDecided, style, drawerZIndex }) => {
  const [rows, reload] = useAcquisitionsToConfirm(companyId, !hidden);
  const [open, setOpen] = React.useState(false);
  if (hidden || rows.length === 0) return null;
  const n = rows.length;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', ...(style || {}) }}>
      <button type="button" onClick={() => setOpen(true)}
        title="Something says this company was acquired — open it to confirm or reject"
        style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
        {window.PhoneHit && <window.PhoneHit />}
        <window.Pill hue="amber" sz="sm" dot>Acquired? · {n === 1 ? 'confirm' : `${n} to confirm`}</window.Pill>
      </button>
      <AcquisitionConfirmDrawer open={open} companyName={companyName} rows={rows} zIndex={drawerZIndex}
        onClose={() => setOpen(false)}
        onDecided={async (decision, r) => {
          await reload();
          if (onDecided) await onDecided(decision, r);
        }} />
    </span>
  );
};

Object.assign(window, { useAcquisitionsToConfirm, AcquisitionConfirmDrawer, AcquisitionHeaderPill, ACQ_SOURCE_LABEL });
